import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackClarityEvent } from '../lib/observability';
import {
  Building2,
  FileText,
  Package,
  Users,
  Landmark,
  BarChart3,
  ShoppingCart,
  Calculator,
  Check,
  ArrowRight,
  Shield,
  Zap,
  Globe,
  ClipboardCheck,
  Rocket,
  HeadphonesIcon,
} from 'lucide-react';
import PublicFooter from '../components/public/PublicFooter';

const features = [
  {
    icon: FileText,
    title: 'Smart Invoicing',
    description: 'Create Nepal-compliant tax invoices with VAT 13%, amount in words, and PDF download.',
  },
  {
    icon: Landmark,
    title: 'Digital Khata',
    description: 'Double-entry bookkeeping with running balance, WhatsApp reminders, and statement PDFs.',
  },
  {
    icon: ShoppingCart,
    title: 'Point of Sale',
    description: 'Session-based checkout with hold/resume, multiple payment methods, and real-time inventory.',
  },
  {
    icon: Package,
    title: 'Inventory Management',
    description: 'Multi-warehouse stock tracking, batch management, transfers, and low-stock alerts.',
  },
  {
    icon: Users,
    title: 'HR & Payroll',
    description: 'Employee records, attendance, leave management, and Nepal-specific PF/SSF/TDS payroll.',
  },
  {
    icon: Landmark,
    title: 'Banking & Cash Book',
    description: 'Track cash flow across multiple accounts with automatic entries from invoices and expenses.',
  },
  {
    icon: Calculator,
    title: 'Expense Tracking',
    description: 'Categorized expenses with recurring templates and automatic cash book integration.',
  },
  {
    icon: BarChart3,
    title: 'Business Reports',
    description: 'Sales summaries, profit & loss, receivables aging, inventory valuation, and VAT reports.',
  },
];

const useCases = [
  {
    title: 'For retailers still using paper khata',
    description: 'Replace manual billing and stock confusion with one workflow for products, sales, invoices, and collections.',
    points: ['Start with a small catalog or sample data', 'Run a first sale with POS or invoicing', 'Review inventory and revenue together'],
  },
  {
    title: 'For wholesalers managing credit sales',
    description: 'Track customers, invoices, receivables, and digital khata without rebuilding the numbers in separate ledgers.',
    points: ['Create customer records once', 'Generate VAT-ready invoices', 'Follow receivables through one shared system'],
  },
  {
    title: 'For SMEs struggling with owner visibility',
    description: 'Give owners a clearer view of invoicing, expenses, payroll, and reports without waiting on month-end spreadsheet cleanup.',
    points: ['Use role-based dashboards', 'Keep collections and billing connected', 'Review reports from one workspace'],
  },
];

const nepalFeatures = [
  'NPR currency with Nepali Rupee formatting',
  'Bikram Sambat (BS) calendar date support',
  'eSewa, Khalti, and ConnectIPS payment modes',
  '13% VAT calculation and reporting',
  'PF, SSF, and TDS payroll deductions',
  'Citizenship and passport validation',
  'Nepal province and district addressing',
  'WhatsApp payment reminders in Nepali context',
];

const pricingPlans = [
  {
    name: 'Starter',
    price: '1,500',
    period: '/month',
    description: 'Best for solo traders and very small businesses',
    features: ['Invoicing', 'Khata', 'Basic Reports', '1 User'],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Business',
    price: '4,500',
    period: '/month',
    description: 'Best for retail shops and growing teams',
    features: ['All Starter features', 'POS', 'Inventory', 'Expenses', '5 Users'],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'Professional',
    price: '9,500',
    period: '/month',
    description: 'Best for larger operations needing deeper control',
    features: ['All Business features', 'HR & Payroll', 'Banking', 'Projects', '15 Users'],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Enterprise',
    price: '19,500',
    period: '/month',
    description: 'Best for multi-branch or custom rollouts',
    features: ['All Professional features', 'Unlimited Users', 'Priority Support', 'Custom Integrations'],
    cta: 'Contact Sales',
    popular: false,
  },
];

const comparisonRows = [
  { feature: 'VAT 13% invoices', spreadsheet: false, bizcore: true },
  { feature: 'Double-entry accounting', spreadsheet: false, bizcore: true },
  { feature: 'Real-time inventory', spreadsheet: false, bizcore: true },
  { feature: 'Digital khata with reminders', spreadsheet: false, bizcore: true },
  { feature: 'PF/SSF/TDS payroll', spreadsheet: false, bizcore: true },
  { feature: 'Multi-user access', spreadsheet: false, bizcore: true },
  { feature: 'Role-based permissions', spreadsheet: false, bizcore: true },
  { feature: 'Auto backup & security', spreadsheet: false, bizcore: true },
];

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export const Landing = () => {
  useEffect(() => {
    trackClarityEvent('landing-viewed');
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-semibold text-foreground tracking-tight">BizCore</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className={`text-sm font-medium text-muted-foreground hover:text-foreground rounded-sm ${focusRing}`}>Features</a>
              <a href="#how-it-works" className={`text-sm font-medium text-muted-foreground hover:text-foreground rounded-sm ${focusRing}`}>How It Works</a>
              <a href="#nepal" className={`text-sm font-medium text-muted-foreground hover:text-foreground rounded-sm ${focusRing}`}>Nepal Ready</a>
              <a href="#pricing" className={`text-sm font-medium text-muted-foreground hover:text-foreground rounded-sm ${focusRing}`}>Pricing</a>
              <Link to="/contact" className={`text-sm font-medium text-muted-foreground hover:text-foreground rounded-sm ${focusRing}`}>Contact</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login" className={`text-sm font-medium text-muted-foreground hover:text-foreground px-4 py-2 rounded-md ${focusRing}`}>
                Sign In
              </Link>
              <Link to="/signup" className={`text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover px-4 py-2 rounded-lg transition-colors ${focusRing}`}>
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-primary-subtle text-primary-subtle-foreground px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Zap className="h-4 w-4" />
            Built for Nepali Businesses
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight mb-6">
            Run your Nepal business on
            <br />
            <span className="text-primary">one platform</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            POS, invoicing, digital khata, inventory, payroll, and reporting — all connected, all Nepal-compliant. Replace spreadsheets, paper khata, and fragmented tools.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" onClick={() => trackClarityEvent('cta-clicked', { location: 'hero' })} className={`inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors text-lg ${focusRing}`}>
              Start Free Trial
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a href="#how-it-works" className={`inline-flex items-center justify-center gap-2 bg-card text-foreground px-8 py-3 rounded-lg font-medium border border-border hover:bg-muted transition-colors text-lg ${focusRing}`}>
              See How It Works
            </a>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            14-day free trial · No card required · Setup in minutes
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              VAT 13% ready
            </span>
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              PF / SSF / TDS payroll
            </span>
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              Digital khata built-in
            </span>
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              Multi-user & role-based
            </span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-4">Get started in 3 steps</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From signup to your first sale — BizCore guides you through the process.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                icon: ClipboardCheck,
                title: 'Sign up & set up',
                description: 'Create your account, add your business details, and configure your chart of accounts. The setup wizard walks you through each step.',
              },
              {
                step: '2',
                icon: Rocket,
                title: 'Add your products & team',
                description: 'Import your product catalog, set up warehouses, add employees, and configure roles and permissions.',
              },
              {
                step: '3',
                icon: HeadphonesIcon,
                title: 'Start selling & tracking',
                description: 'Run your first POS sale, send your first invoice, or record your first expense. Reports update in real time.',
              },
            ].map((item) => (
              <div key={item.step} className="relative bg-card p-8 rounded-2xl border border-border shadow-sm">
                <div className="absolute -top-4 left-8 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold tabular">
                  {item.step}
                </div>
                <div className="h-12 w-12 rounded-lg bg-primary-subtle flex items-center justify-center mb-4 mt-2">
                  <item.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-4">Everything you need to run your business</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              One system replaces spreadsheets, paper khata, and fragmented tools.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="bg-card p-6 rounded-xl border border-border shadow-sm hover:shadow-md hover:border-border-strong transition-shadow">
                <div className="h-12 w-12 rounded-lg bg-primary-subtle flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-4">Built for how Nepali businesses actually work</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Whether you're a retailer, wholesaler, or growing SME — BizCore fits your workflow.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            {useCases.map((useCase) => (
              <div key={useCase.title} className="rounded-2xl border border-border bg-card p-8 shadow-sm">
                <h3 className="text-xl font-semibold text-foreground mb-3">{useCase.title}</h3>
                <p className="text-sm leading-6 text-muted-foreground mb-6">{useCase.description}</p>
                <ul className="space-y-3">
                  {useCase.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-sm text-foreground">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BizCore vs Spreadsheets */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-4">Stop stitching spreadsheets together</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              If you're tracking sales in one sheet, inventory in another, and expenses in a third — you're wasting hours every week.
            </p>
          </div>
          <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
            <div className="grid grid-cols-3 bg-muted border-b border-border">
              <div className="p-4 text-sm font-semibold text-muted-foreground">Feature</div>
              <div className="p-4 text-sm font-semibold text-muted-foreground text-center hidden sm:block">Spreadsheets</div>
              <div className="p-4 text-sm font-semibold text-primary text-center">BizCore</div>
            </div>
            {comparisonRows.map((row, i) => (
              <div key={row.feature} className={`grid grid-cols-2 sm:grid-cols-3 ${i < comparisonRows.length - 1 ? 'border-b border-border' : ''}`}>
                <div className="p-4 text-sm text-foreground">{row.feature}</div>
                <div className="p-4 text-center hidden sm:block">
                  {row.spreadsheet ? (
                    <Check className="h-5 w-5 text-credit mx-auto" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                <div className="p-4 text-center">
                  <Check className="h-5 w-5 text-primary mx-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nepal-Specific Section */}
      <section id="nepal" className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary-subtle text-primary-subtle-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Globe className="h-4 w-4" />
                Made for Nepal
              </div>
              <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-6">
                Built specifically for Nepali businesses
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Generic SaaS tools don't understand Nepal's business environment.
                BizCore is built from the ground up for how Nepali businesses actually work.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {nepalFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Signature: a ruled khata panel — the ledger, re-set for the screen */}
            <div className="rounded-2xl bg-primary p-8 text-primary-foreground shadow-lg">
              <div className="flex items-center justify-between border-b border-primary-foreground/20 pb-4 mb-2">
                <h3 className="text-lg font-semibold">Why Nepali businesses choose BizCore</h3>
                <Landmark className="h-5 w-5 text-primary-foreground/70" />
              </div>
              <div className="divide-y divide-primary-foreground/15">
                <div className="flex items-start gap-3 py-4">
                  <Shield className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">VAT Ready</p>
                    <p className="text-primary-foreground/70 text-sm">Automatic 13% VAT calculation and reporting for Inland Revenue</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 py-4">
                  <Calculator className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Payroll Made Easy</p>
                    <p className="text-primary-foreground/70 text-sm">PF 10%, SSF, and TDS deductions calculated automatically</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 py-4">
                  <Landmark className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Digital Khata</p>
                    <p className="text-primary-foreground/70 text-sm">Replace paper khata with a digital ledger and WhatsApp reminders</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-4">Simple, transparent pricing</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your business. Upgrade or downgrade anytime.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`bg-card p-6 rounded-xl border-2 ${
                  plan.popular
                    ? 'border-primary shadow-lg shadow-primary/10'
                    : 'border-border'
                } relative`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                <div className="mt-4 mb-6">
                  <span className="text-3xl font-semibold text-foreground tabular">NPR {plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-foreground">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.name === 'Enterprise' ? '/contact' : '/signup'}
                  className={`block w-full text-center py-2.5 rounded-lg font-medium transition-colors ${focusRing} ${
                    plan.popular
                      ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                      : 'bg-muted text-foreground hover:bg-accent'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8">
            All plans include 14-day free trial · No credit card required · Cancel anytime
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold text-primary-foreground tracking-tight mb-4">
            Replace paper khata with a system that grows with you
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8">
            Start with a free trial. No card required. Set up your first invoice, POS sale, or khata entry in minutes.
          </p>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link to="/signup" className={`inline-flex items-center gap-2 bg-card text-primary px-8 py-3 rounded-lg font-medium hover:bg-muted transition-colors text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary`}>
              Start Your Free Trial
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/contact" className={`inline-flex items-center gap-2 text-primary-foreground/80 px-8 py-3 rounded-lg font-medium hover:text-primary-foreground transition-colors text-lg border border-primary-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary`}>
              Talk to Us
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Landing;
